(() => {
  const STELLR_GSAP_VERSION = '3.13.0';
  const contexts = new Map();
  const splitRegistry = new Map();
  let smoother = null;
  let refreshTimer = null;
  let layoutSettleTimer = null;

  const html = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopFinePointer = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');

  const canAnimate = () =>
    html.dataset.designMode !== 'true' &&
    html.dataset.motionEnabled === 'true' &&
    !reduceMotion.matches &&
    desktopFinePointer.matches &&
    Boolean(window.gsap && window.ScrollTrigger);

  const configureScrollTriggerAutoRefresh = () => {
    if (!window.ScrollTrigger?.config) return;

    window.ScrollTrigger.config({
      // Safari changes the layout viewport when its browser chrome collapses. On mobile
      // there are no ScrollTriggers to refresh, so leave that viewport entirely native.
      autoRefreshEvents: canAnimate()
        ? 'visibilitychange,DOMContentLoaded,load,resize'
        : 'visibilitychange,DOMContentLoaded,load',
    });
  };

  const motionItems = (root) => {
    const items = root.querySelectorAll('[data-motion-item]');
    return items.length ? [...items] : [root];
  };

  const revealMotionItems = (root = document) => {
    const items = root instanceof Element && root.matches('[data-motion-item]')
      ? [root]
      : [...root.querySelectorAll('[data-motion-item]')];

    if (!items.length) return;

    if (window.gsap) {
      window.gsap.set(items, { autoAlpha: 1 });
    } else {
      items.forEach((item) => {
        item.style.opacity = '1';
        item.style.visibility = 'visible';
      });
    }
  };

  const shouldSplitText = (element) =>
    element &&
    element.textContent.trim() &&
    !element.dataset.stellrSplit &&
    !element.closest('[data-stellr-no-split]') &&
    !element.querySelector('a, button, input, select, textarea, img, svg, video, iframe');

  const rememberSplit = (element) => {
    if (!splitRegistry.has(element)) splitRegistry.set(element, element.innerHTML);
    element.dataset.stellrSplit = 'true';
  };

  const restoreSplits = (root = document) => {
    for (const [element, htmlContent] of splitRegistry.entries()) {
      const matchesRoot = root === document || root === element || (root instanceof Element && root.contains(element));
      if (!matchesRoot) continue;
      element.innerHTML = htmlContent;
      delete element.dataset.stellrSplit;
      splitRegistry.delete(element);
    }
  };

  const splitWords = (element) => {
    if (!shouldSplitText(element)) return [];

    const words = element.textContent.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return [];

    rememberSplit(element);
    element.textContent = '';

    return words.map((word, index) => {
      const wrapper = document.createElement('span');
      wrapper.className = 'stellr-split-word';
      wrapper.textContent = word;
      element.append(wrapper);
      if (index < words.length - 1) element.append(document.createTextNode(' '));
      return wrapper;
    });
  };

  const splitLines = (element) => {
    if (!shouldSplitText(element)) return [];

    const words = element.textContent.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return [];

    rememberSplit(element);
    element.textContent = '';

    const wordSpans = words.map((word, index) => {
      const wrapper = document.createElement('span');
      wrapper.className = 'stellr-split-word';
      wrapper.textContent = word;
      element.append(wrapper);
      if (index < words.length - 1) element.append(document.createTextNode(' '));
      return wrapper;
    });

    const groups = [];
    wordSpans.forEach((word) => {
      const top = Math.round(word.offsetTop);
      const current = groups[groups.length - 1];
      if (!current || Math.abs(current.top - top) > 2) {
        groups.push({ top, words: [word] });
      } else {
        current.words.push(word);
      }
    });

    element.textContent = '';
    return groups.map((group, groupIndex) => {
      const line = document.createElement('span');
      line.className = 'stellr-split-line';
      group.words.forEach((word, wordIndex) => {
        line.append(word);
        if (wordIndex < group.words.length - 1) line.append(document.createTextNode(' '));
      });
      element.append(line);
      if (groupIndex < groups.length - 1) element.append(document.createTextNode(' '));
      return line;
    });
  };

  const uniqueElements = (elements) => [...new Set(elements.filter(Boolean))];

  const queryTextParts = (root) => {
    const items = motionItems(root);
    const headings = uniqueElements(
      items.flatMap((item) => [
        ...(item.matches?.('h1, h2, h3') ? [item] : []),
        ...item.querySelectorAll('h1, h2, h3'),
      ]),
    ).filter((element) => !element.closest('[data-stellr-no-split]'));
    const body = uniqueElements(
      items.flatMap((item) => [
        ...item.querySelectorAll(
          '.stellr-story__body p, .stellr-hero__body p, .stellr-rte p, [class*="__body"] p',
        ),
      ]),
    ).filter((element) => !element.closest('form') && !element.closest('[data-stellr-no-split]'));

    return {
      headingWords: headings.flatMap(splitWords),
      bodyLines: body.flatMap(splitLines),
    };
  };

  const splitRevealVars = (offset = 24) => ({
    y: offset,
    autoAlpha: 0,
    filter: 'blur(10px)',
  });

  const animateTextReveal = (root, vars = {}) => {
    const textParts = queryTextParts(root);
    const textTargets = new Set([...textParts.headingWords, ...textParts.bodyLines]);
    const fallbackItems = motionItems(root).filter((item) => ![...textTargets].some((target) => item.contains(target)));
    const timeline = window.gsap.timeline({
      scrollTrigger: { trigger: root, start: vars.start || 'top 82%', once: true },
    });

    if (textParts.headingWords.length) {
      timeline.from(textParts.headingWords, {
        ...splitRevealVars(28),
        duration: 0.82,
        ease: 'power3.out',
        stagger: 0.035,
      });
    }

    if (textParts.bodyLines.length) {
      timeline.from(
        textParts.bodyLines,
        {
          ...splitRevealVars(18),
          duration: 0.72,
          ease: 'power3.out',
          stagger: 0.07,
        },
        textParts.headingWords.length ? '-=0.45' : 0,
      );
    }

    if (fallbackItems.length) {
      timeline.from(
        fallbackItems,
        {
          y: 24,
          autoAlpha: 0,
          filter: 'blur(8px)',
          duration: 0.72,
          stagger: vars.stagger || 0.08,
          ease: 'power3.out',
        },
        textParts.headingWords.length || textParts.bodyLines.length ? '-=0.35' : 0,
      );
    }

    return timeline;
  };

  const animateBackgroundParallax = (root) => {
    const media = [...root.querySelectorAll('[data-stellr-parallax-media]')];
    media.forEach((element, index) => {
      const targets = element.matches('img, video, svg')
        ? [element]
        : [...element.querySelectorAll('img, video, svg')];

      if (!targets.length) return;

      window.gsap.set(targets, {
        scale: 1.1,
        yPercent: -5,
        transformOrigin: '50% 50%',
        force3D: true,
      });

      window.gsap.to(targets, {
        yPercent: 5,
        xPercent: index % 2 === 0 ? 1.5 : -1.5,
        ease: 'none',
        scrollTrigger: {
          trigger: root,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.8,
          invalidateOnRefresh: true,
        },
      });
    });
  };

  const animateFallingStars = (root) => {
    const stars = [...root.querySelectorAll('[data-stellr-falling-star]')].filter((star) => {
      const computed = window.getComputedStyle(star);
      return computed.display !== 'none' && computed.visibility !== 'hidden';
    });
    if (!stars.length) return;

    const starTimeline = window.gsap.timeline({
      defaults: { duration: 1, ease: 'none' },
      scrollTrigger: {
        trigger: root,
        start: 'top 80%',
        end: 'bottom top',
        scrub: 0.8,
        invalidateOnRefresh: true,
      },
    });

    stars.forEach((star, index) => {
      const computed = window.getComputedStyle(star);
      const rotation = Number.parseFloat(computed.getPropertyValue('--rotate')) || 48;
      const direction = index % 2 === 0 ? 1 : -1;
      const sectionTravel = 0.25 + (index % 5) * 0.14;

      starTimeline.fromTo(
        star,
        { y: 0, rotation: direction * -8, force3D: true },
        {
          y: () => root.offsetHeight * sectionTravel,
          rotation: direction * rotation,
          force3D: true,
        },
        0,
      );
    });
  };

  const createAnimation = (root) => {
    const preset = root.dataset.stellrMotion || 'none';
    const hasBackgroundParallax = Boolean(root.querySelector('[data-stellr-parallax-media]'));
    const hasFallingStars = Boolean(root.querySelector('[data-stellr-falling-star]'));
    if (preset === 'none' && !hasBackgroundParallax && !hasFallingStars) return null;

    restoreSplits(root);

    return window.gsap.context(() => {
      revealMotionItems(root);
      animateBackgroundParallax(root);
      if (preset !== 'floating_testimonials') animateFallingStars(root);

      if (preset === 'reveal') {
        animateTextReveal(root);
      }

      if (preset === 'stagger') {
        animateTextReveal(root, { start: 'top 80%', stagger: 0.1 });
      }

      if (preset === 'parallax') {
        const media = root.querySelector('[data-motion-media]');
        if (media) {
          window.gsap.fromTo(
            media,
            { yPercent: -5 },
            {
              yPercent: 5,
              ease: 'none',
              scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom top', scrub: true },
            },
          );
        }
      }

      if (preset === 'variant_showcase') {
        const heading = root.querySelector('.stellr-story__pin-heading');
        const cards = [...root.querySelectorAll('.stellr-story__variant-card')];

        if (heading) {
          window.gsap.from(heading, {
            y: 28,
            autoAlpha: 0,
            duration: 0.78,
            ease: 'power3.out',
            force3D: true,
            scrollTrigger: { trigger: root, start: 'top 78%', once: true },
          });
        }

        if (cards.length) {
          window.gsap.from(cards, {
            y: 38,
            autoAlpha: 0,
            duration: 0.86,
            stagger: 0.1,
            ease: 'power3.out',
            force3D: true,
            scrollTrigger: { trigger: root, start: 'top 70%', once: true },
          });
        }

      }

      if (preset === 'about_scroll_story') {
        const pin = root.querySelector('[data-about-scroll-story-pin]');
        const copy = root.querySelector('[data-about-scroll-story-copy]');
        const lanes = [...root.querySelectorAll('[data-about-scroll-lane]')];
        const media = [...root.querySelectorAll('[data-about-scroll-media]')];
        const stars = [...root.querySelectorAll('[data-about-scroll-star]')];

        if (pin && media.length) {
          root.classList.add('stellr-about-manifesto--motion-ready');

          window.ScrollTrigger.create({
            trigger: root,
            start: 'top top',
            end: 'bottom bottom',
            pin,
            pinSpacing: false,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          });

          if (copy) {
            window.gsap.fromTo(
              copy,
              { y: 28, autoAlpha: 0, filter: 'blur(10px)' },
              {
                y: 0,
                autoAlpha: 1,
                filter: 'blur(0px)',
                ease: 'none',
                scrollTrigger: {
                  trigger: root,
                  start: 'top 72%',
                  end: 'top 20%',
                  scrub: 0.45,
                  invalidateOnRefresh: true,
                },
              },
            );
          }

          media.forEach((frame, index) => {
            const lane = lanes[index] || frame.parentElement;
            const position = frame.dataset.aboutScrollPosition || 'left';
            const direction = position === 'right' ? 1 : position === 'left' ? -1 : index % 2 === 0 ? -1 : 1;

            window.gsap.fromTo(
              frame,
              {
                xPercent: direction * 8,
                yPercent: 16,
                scale: 0.93,
                autoAlpha: 0.48,
              },
              {
                xPercent: direction * -5,
                yPercent: -16,
                scale: 1.035,
                autoAlpha: 1,
                ease: 'none',
                scrollTrigger: {
                  trigger: lane,
                  start: 'top bottom',
                  end: 'bottom top',
                  scrub: 0.55,
                  invalidateOnRefresh: true,
                },
              },
            );
          });

          stars.forEach((star, index) => {
            window.gsap.to(star, {
              y: index % 2 === 0 ? -90 : 110,
              rotation: index % 2 === 0 ? 74 : -82,
              ease: 'none',
              scrollTrigger: {
                trigger: root,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.8,
                invalidateOnRefresh: true,
              },
            });
          });
        }
      }

      if (preset === 'pinned_product_story') {
        const heading = root.querySelector('.stellr-story__pin-heading');
        const primaryProduct = root.querySelector('[data-pinned-product-primary]');
        const commerce = root.querySelector('[data-pinned-commerce]');
        const commerceLeft = root.querySelector('[data-pinned-commerce-left]');
        const commerceRight = root.querySelector('[data-pinned-commerce-right]');
        const logo = root.querySelector('[data-pinned-logo]');

        if (primaryProduct && commerce) {
          const introTargets = [heading, logo].filter(Boolean);
          const commerceTitleWords = commerceLeft ? splitWords(commerceLeft.querySelector('h3')) : [];
          const commerceBodyLines = commerceRight
            ? [...commerceRight.querySelectorAll('.stellr-story__body p')].flatMap(splitLines)
            : [];
          const commerceSupport = commerceRight
            ? [...commerceRight.children].filter((child) => !child.matches('.stellr-story__body'))
            : [];

          window.gsap.set(commerce, { autoAlpha: 1 });
          window.gsap.set(commerceLeft, { x: -72, y: 20 });
          window.gsap.set(commerceRight, { x: 72, y: 20 });
          window.gsap.set([...commerceTitleWords, ...commerceBodyLines], splitRevealVars(22));
          window.gsap.set(commerceSupport, { y: 18, autoAlpha: 0, filter: 'blur(8px)' });

          const timeline = window.gsap.timeline({
            defaults: { ease: 'none' },
            scrollTrigger: {
              trigger: root,
              start: 'top top',
              end: () => `+=${Math.round(window.innerHeight * 1.15)}`,
              scrub: 0.25,
              pin: true,
              anticipatePin: 1,
              invalidateOnRefresh: true,
            },
          });

          timeline
            .fromTo(
              primaryProduct,
              {
                x: 0,
                y: 0,
                rotation: -8,
                scale: 0.72,
              },
              {
                x: () => window.innerWidth * -0.045,
                y: () => window.innerHeight * 0.025,
                rotation: -4,
                scale: 1.03,
              },
              0,
            );

          timeline
            .to(introTargets, { y: () => window.innerHeight * -0.34 }, 0)
            .to([commerceLeft, commerceRight].filter(Boolean), { x: 0, y: 0 }, 0.5)
            .to(commerceTitleWords, { y: 0, autoAlpha: 1, filter: 'blur(0px)', stagger: 0.045 }, 0.54)
            .to(commerceBodyLines, { y: 0, autoAlpha: 1, filter: 'blur(0px)', stagger: 0.08 }, 0.64)
            .to(commerceSupport, { y: 0, autoAlpha: 1, filter: 'blur(0px)', stagger: 0.06 }, 0.74);
        }
      }

      if (preset === 'floating_testimonials') {
        const rail = root.querySelector('[data-testimonial-rail]');
        const cards = rail ? [...rail.children] : [];
        const pin = root.querySelector('[data-testimonial-pin]');
        const stars = [...root.querySelectorAll('[data-stellr-falling-star]')].filter((star) => {
          const computed = window.getComputedStyle(star);
          return computed.display !== 'none' && computed.visibility !== 'hidden';
        });
        const canUseDesktopFloatingTestimonials = window.matchMedia('(min-width: 750px)').matches;
        if (rail && pin && cards.length && canUseDesktopFloatingTestimonials) {
          root.classList.add('stellr-testimonials--motion-ready');

          window.ScrollTrigger.create({
            trigger: root,
            start: 'top top',
            end: 'bottom bottom',
            pin,
            pinSpacing: false,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          });

          cards.forEach((card, index) => {
            const direction = index % 2 === 0 ? -1 : 1;

            window.gsap.fromTo(
              card,
              {
                xPercent: direction * 9,
                yPercent: 10,
                scale: 0.97,
                autoAlpha: 1,
                force3D: true,
              },
              {
                xPercent: direction * -6,
                yPercent: -10,
                scale: 1.015,
                autoAlpha: 1,
                ease: 'none',
                force3D: true,
                scrollTrigger: {
                  trigger: card,
                  start: 'top bottom',
                  end: 'bottom top',
                  scrub: 0.5,
                  invalidateOnRefresh: true,
                },
              },
            );
          });

          if (stars.length) {
            const starTimeline = window.gsap.timeline({
              defaults: { duration: 1, ease: 'none' },
              scrollTrigger: {
                trigger: root,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.8,
                invalidateOnRefresh: true,
              },
            });

            stars.forEach((star, index) => {
              const rotation = Number.parseFloat(window.getComputedStyle(star).getPropertyValue('--rotate')) || 48;
              const direction = index % 2 === 0 ? 1 : -1;

              starTimeline.to(
                star,
                {
                  y: () => direction * window.innerHeight * (0.16 + (index % 4) * 0.05),
                  rotation: direction * rotation,
                  force3D: true,
                },
                0,
              );
            });
          }
        }
      }

      if (preset === 'stacked_cards') {
        const cards = [...root.querySelectorAll('[data-motion-card]')];
        cards.forEach((card, index) => {
          window.gsap.from(card, {
            y: 48 + index * 14,
            autoAlpha: 0,
            duration: 0.7,
            delay: index * 0.08,
            ease: 'power3.out',
            scrollTrigger: { trigger: card, start: 'top 88%', once: true },
          });
        });
      }

      if (preset === 'affiliate_story') {
        const mediaCards = [...root.querySelectorAll('[data-affiliate-media-card]')];
        const steps = [...root.querySelectorAll('[data-affiliate-step]')];
        const noteMedia = root.querySelector('[data-affiliate-note-media]');
        const offerStar = root.querySelector('[data-affiliate-star]');

        mediaCards.forEach((card, index) => {
          window.gsap.fromTo(
            card,
            {
              y: 44 + index * 14,
              scale: 0.94,
              autoAlpha: 0.5,
            },
            {
              y: 0,
              scale: 1,
              autoAlpha: 1,
              ease: 'none',
              scrollTrigger: {
                trigger: card,
                start: 'top 92%',
                end: 'top 42%',
                scrub: 0.45,
                invalidateOnRefresh: true,
              },
            },
          );
        });

        if (steps.length) {
          window.gsap.from(steps, {
            y: 72,
            autoAlpha: 0,
            scale: 0.96,
            duration: 0.82,
            stagger: 0.1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: steps[0].parentElement,
              start: 'top 82%',
              once: true,
            },
          });
        }

        if (noteMedia) {
          window.gsap.fromTo(
            noteMedia,
            { scale: 0.92, autoAlpha: 0.68 },
            {
              scale: 1,
              autoAlpha: 1,
              ease: 'none',
              scrollTrigger: {
                trigger: noteMedia,
                start: 'top 94%',
                end: 'top 38%',
                scrub: 0.5,
                invalidateOnRefresh: true,
              },
            },
          );
        }

        if (offerStar) {
          window.gsap.fromTo(
            offerStar,
            { rotation: -12, yPercent: 16 },
            {
              rotation: 14,
              yPercent: -10,
              ease: 'none',
              scrollTrigger: {
                trigger: offerStar.closest('[data-affiliate-offer]') || offerStar,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.75,
                invalidateOnRefresh: true,
              },
            },
          );
        }
      }
    }, root);
  };

  const destroy = (root = document) => {
    for (const [element, context] of contexts.entries()) {
      const matchesRoot = root === document || root === element || (root instanceof Element && root.contains(element));
      if (!matchesRoot) continue;
      context?.revert();
      element.classList.remove('stellr-about-manifesto--motion-ready');
      element.classList.remove('stellr-testimonials--motion-ready');
      contexts.delete(element);
    }
    restoreSplits(root);
  };

  const initSmoother = () => {
    smoother?.kill();
    smoother = null;

    if (!canAnimate() || html.dataset.smootherEnabled !== 'true' || !window.ScrollSmoother) return;

    window.gsap.registerPlugin(window.ScrollTrigger, window.ScrollSmoother);
    const smooth = Math.max(0.2, Number(html.dataset.smootherStrength || 8) / 10);
    smoother = window.ScrollSmoother.create({
      wrapper: '#StellrSmoother',
      content: '#StellrSmootherContent',
      smooth,
      effects: true,
      smoothTouch: false,
    });
  };

  const init = (root = document) => {
    if (!canAnimate()) {
      revealMotionItems(root);
      html.classList.add('stellr-motion-ready');
      return;
    }
    window.gsap.registerPlugin(window.ScrollTrigger);

    const candidates = [];
    if (root instanceof Element && root.matches('[data-stellr-motion]')) candidates.push(root);
    candidates.push(...root.querySelectorAll('[data-stellr-motion]'));
    const parallaxMedia = root instanceof Element && root.matches('[data-stellr-parallax-media]')
      ? [root]
      : [...root.querySelectorAll('[data-stellr-parallax-media]')];
    parallaxMedia.forEach((media) => {
      const owner = media.closest('[data-section-id]') || media;
      if (!candidates.includes(owner)) candidates.push(owner);
    });
    const fallingStars = root instanceof Element && root.matches('[data-stellr-falling-star]')
      ? [root]
      : [...root.querySelectorAll('[data-stellr-falling-star]')];
    fallingStars.forEach((star) => {
      const owner = star.closest('[data-section-id]') || star;
      if (!candidates.includes(owner)) candidates.push(owner);
    });

    for (const element of candidates) {
      contexts.get(element)?.revert();
      const context = createAnimation(element);
      if (context) contexts.set(element, context);
    }

    html.classList.add('stellr-motion-ready');
  };

  const refresh = (delay = 120) => {
    if (!canAnimate()) return;
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => {
      if (!canAnimate()) return;
      smoother?.refresh?.();
      window.ScrollTrigger?.refresh(true);
    }, delay);
  };

  const refreshAfterLayoutSettles = () => {
    if (!canAnimate()) return;
    window.clearTimeout(layoutSettleTimer);
    layoutSettleTimer = window.setTimeout(() => refresh(0), 180);
  };

  const initializePage = () => {
    configureScrollTriggerAutoRefresh();
    initSmoother();
    init(document);
    if (!canAnimate()) return;

    document.fonts?.ready.then(refreshAfterLayoutSettles);
    refreshAfterLayoutSettles();
  };

  const resyncPage = () => {
    configureScrollTriggerAutoRefresh();
    initSmoother();
    init(document);
    refreshAfterLayoutSettles();
  };

  window.StellrMotion = { version: STELLR_GSAP_VERSION, init, destroy, refresh };

  document.addEventListener('DOMContentLoaded', initializePage);
  document.addEventListener('shopify:section:load', (event) => {
    destroy(event.target);
    init(event.target);
    refreshAfterLayoutSettles();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
    refreshAfterLayoutSettles();
  });
  document.addEventListener('shopify:section:reorder', refreshAfterLayoutSettles);
  window.addEventListener('load', refreshAfterLayoutSettles, { passive: true });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) resyncPage();
    else refreshAfterLayoutSettles();
  });
  const refreshDesktopMotion = () => {
    if (canAnimate()) refreshAfterLayoutSettles();
  };

  window.addEventListener('resize', refreshDesktopMotion, { passive: true });
  window.addEventListener('orientationchange', refreshDesktopMotion, { passive: true });
  const handleMotionPreferenceChange = () => {
    destroy(document);
    configureScrollTriggerAutoRefresh();
    initSmoother();
    init(document);
    refreshAfterLayoutSettles();
  };

  reduceMotion.addEventListener('change', handleMotionPreferenceChange);
  desktopFinePointer.addEventListener('change', handleMotionPreferenceChange);
})();
